/**
 * The small bars the Capital Stack tab draws beside its figures: a gauge on a
 * scale (Coverage & Sizing), a covenant's headroom (Covenants & Record), a
 * line's share of its column (Sources & Uses) and a tranche's place in the stack
 * (Capitalization). Every one is the number printed beside it, drawn — see
 * capitalStackGauges.ts for what is and is not a scale.
 *
 * A fill repeats the value state of the figure it belongs to (the chip's own
 * colors), and an implied figure is hatched, as in the stack bar, so a derived
 * or implied figure never looks like a stated one.
 */
import type { CSSProperties } from 'react'

import { FIELD_STATE_TOKENS } from '@/components/fields/fieldState'
import type { CapSegment } from './leverageModel'
import { covenantHeadroom, type Gauge, type GaugeId, type GaugeState } from './capitalStackGauges'
import type { CovenantRow } from './capitalStackModel'

const TRACK = 'bg-white/[0.07]'

/** The fill of a gauge, by value state: the chip's dot color; implied is hatched. */
const FILL_CLASS: Record<GaugeState, string> = {
  stated: FIELD_STATE_TOKENS.stated.dot,
  derived: FIELD_STATE_TOKENS.derived.dot,
  confirmed: FIELD_STATE_TOKENS.confirmed.dot,
  suggested: FIELD_STATE_TOKENS.suggested.dot,
  implied: 'bg-sky-300',
}

function fillStyle(state: GaugeState, width: number): CSSProperties {
  const base: CSSProperties = { width: `${(width * 100).toFixed(2)}%` }
  if (state !== 'implied') return base
  return {
    ...base,
    backgroundImage: 'repeating-linear-gradient(45deg, rgba(10,10,10,0.55) 0 2px, transparent 2px 5px)',
  }
}

/** A figure on its scale: a fill, the marks the record states, the scale's end. */
export function GaugeBar({ id, gauge, state }: { id: GaugeId; gauge: Gauge; state: GaugeState }) {
  return (
    <div data-gauge={id} data-gauge-state={state} role="img" aria-label={gauge.title} title={gauge.title} className="w-full min-w-0">
      <div className="relative h-[10px]">
        <div className={`absolute inset-x-0 top-[2px] h-[6px] rounded-full ${TRACK}`}>
          <div
            data-gauge-fill=""
            className={`h-full rounded-full opacity-90 ${FILL_CLASS[state]}`}
            style={fillStyle(state, gauge.fill)}
          />
        </div>
        {gauge.marks.map((mark) => (
          <span
            key={`${mark.kind}-${mark.at}`}
            data-gauge-mark={mark.kind}
            title={mark.title}
            className={`absolute top-0 h-[10px] w-px ${mark.kind === 'limit' ? 'bg-amber-300' : 'bg-stone-200/80'}`}
            style={{ left: `${(mark.at * 100).toFixed(2)}%` }}
          />
        ))}
      </div>
      <div className="cs-scale-label mt-0.5 h-[9px] text-right font-mono text-[7px] leading-[9px] text-stone-600">{gauge.scaleLabel}</div>
    </div>
  )
}

/** A covenant's figure against its limit: the fill is the deal's figure, the
 *  tick is the limit the record states, and the fill's color says which side of
 *  it the figure is on (compliant, in breach, or not tested). No new number is
 *  printed: the row's own Requirement and Current say them. */
export function HeadroomBar({ row }: { row: CovenantRow }) {
  const headroom = covenantHeadroom(row)
  if (!headroom) return null
  const tone = headroom.compliant == null ? 'bg-stone-400' : headroom.compliant ? 'bg-emerald-400' : 'bg-red-400'
  return (
    <div
      data-headroom={row.id}
      data-headroom-state={headroom.compliant == null ? 'untested' : headroom.compliant ? 'meets' : 'breach'}
      role="img"
      aria-label={headroom.title}
      title={headroom.title}
      className="relative mt-1 h-[7px] w-full min-w-[44px]"
    >
      <div className={`absolute inset-x-0 top-[2px] h-[3px] rounded-full ${TRACK}`}>
        <div className={`h-full rounded-full opacity-90 ${tone}`} style={{ width: `${(headroom.fill * 100).toFixed(2)}%` }} />
      </div>
      <span
        data-headroom-limit=""
        className="absolute top-0 h-[7px] w-px bg-amber-300"
        style={{ left: `${(headroom.limitAt * 100).toFixed(2)}%` }}
      />
    </div>
  )
}

/** A line's share of its column's total, under the line. */
export function ShareBar({ share, color, implied = false }: { share: number; color?: string; implied?: boolean }) {
  const background = color ?? '#c49a43'
  return (
    <div data-share={(share * 100).toFixed(1)} aria-hidden="true" className={`h-[3px] w-full rounded-full ${TRACK}`}>
      <div
        className="h-full rounded-full"
        style={
          implied
            ? {
                width: `${(share * 100).toFixed(2)}%`,
                backgroundImage: `repeating-linear-gradient(45deg, ${background} 0 2px, transparent 2px 5px)`,
              }
            : { width: `${(share * 100).toFixed(2)}%`, background }
        }
      />
    </div>
  )
}

/** A tranche's place in the stack on a 0–100 track: attachment to detachment. */
export function AttachmentTrack({ segment }: { segment: Pick<CapSegment, 'attach' | 'detach' | 'color' | 'isImplied'> }) {
  const left = Math.min(100, Math.max(0, segment.attach))
  const width = Math.max(0.8, Math.min(100 - left, segment.detach - segment.attach))
  return (
    <div
      data-attachment-track=""
      data-attach={segment.attach.toFixed(1)}
      data-detach={segment.detach.toFixed(1)}
      aria-hidden="true"
      className={`relative h-[3px] w-full min-w-[56px] rounded-full ${TRACK}`}
    >
      <div
        className="absolute inset-y-0 rounded-full"
        style={{
          left: `${left.toFixed(2)}%`,
          width: `${width.toFixed(2)}%`,
          ...(segment.isImplied
            ? { backgroundImage: `repeating-linear-gradient(45deg, ${segment.color} 0 2px, transparent 2px 5px)` }
            : { background: segment.color }),
        }}
      />
    </div>
  )
}
